import { ArrowLeft, MapPin, Mountain, Utensils, Wifi, X, ZoomIn } from 'lucide-react';
import { FaHeart } from 'react-icons/fa';
import { useContext, useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import '../../../../assets/css/customer/hotelspages/hoteldetailscreen.css';
import AppContext from '../../../../context/AppContext';
import CustomerLoginForm from '../../../../components/customer/PublicCustomerScreens/CustomerFormComponents/CustomerLoginForm';

const facilitiesOptions = [
  { id: 'wifi',       label: 'Free WiFi' },
  { id: 'parking',    label: 'Free Parking' },
  { id: 'breakfast',  label: 'Free Breakfast' },
  { id: 'gym',        label: 'Gym' },
  { id: 'tv',         label: 'TV in Rooms' },
  { id: 'pool',       label: 'Swimming Pool' },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'ac',         label: 'AC Rooms' },
  { id: 'security',   label: '24/7 Security' },
];
const renderAmenities = (amenities) => {
  if (!amenities) return [];
  if (Array.isArray(amenities)) return amenities;
  if (typeof amenities === 'string') return amenities.split(',').map(i => i.trim());
  return [];
};

function HotelDetailsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const hotel = location.state?.hotel;
  const { baseUrl, authToken } = useContext(AppContext);

  const [checkInDate,  setCheckInDate]  = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [zoomedImg,    setZoomedImg]    = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!authToken || !hotel) return;

    const currentCustomerId = Number(localStorage.getItem('customerId'));
    const currentHotelId    = Number(hotel.id);

    axios.get(`${baseUrl}/customer/wishlist`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => {
        if (res.data.success) {
          const found = res.data.data.some(
            (item) =>
              Number(item.jjcustomer_id) === currentCustomerId &&
              Number(item.jjhotelregistration_id) === currentHotelId
          );
          setIsWishlisted(found);
        }
      })
      .catch((err) => console.error('Fetch wishlist error:', err));
  }, [baseUrl, authToken, hotel]);

  const handleWishlistToggle = () => {
    if (!authToken) {
      setShowLoginModal(true);
      return;
    }

    const numericHotelId = Number(hotel.id);

    if (isWishlisted) {
      axios.delete(`${baseUrl}/customer/wishlist/${numericHotelId}?type=hotel`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(() => setIsWishlisted(false))
        .catch((err) => console.error('Remove wishlist error:', err));
    } else {
      const formData = new FormData();
      formData.append('jjhotelregistration_id', numericHotelId);

      axios.post(`${baseUrl}/customer/addWishlist`, formData, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(() => setIsWishlisted(true))
        .catch((err) => console.error('Add wishlist error:', err));
    }
  };
  if (!hotel) { navigate('/hotels'); return null; }

  const handleBookNow = () => {
    const token = localStorage.getItem("authToken");
    const customerId = localStorage.getItem("customerId");

    if (!token || !customerId) {
        localStorage.setItem("pendingBookingHotel", hotel.id);

        Swal.fire({
            title: 'Login Required',
            html: `
                <div style="font-size:15px;color:#555">
                    Please login first to continue your booking.
                    <br/>
                    After login, you will be redirected back here.
                </div>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Login Now',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#2C5F7C',
            cancelButtonColor: '#999',
            allowOutsideClick: false,
            width: '420px',
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Redirecting...',
                    html: `<div>Taking you to login page</div>`,
                    timer: 1200,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    didOpen: () => { Swal.showLoading(); }
                }).then(() => { navigate('/auth/login'); });
            }
        });
        return;
    }
    if (!checkInDate || !checkOutDate) {
      alert('Please select check-in and check-out dates');
      return;
    }
    if (!selectedRoom) {
      alert('Please select a room type');
      return;
    }
    const checkIn  = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const nights   = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    if (nights <= 0) {
      alert('Check-out date must be after check-in date');
      return;
    }

    navigate('/customer-booking', {
      state: {
        hotel,
        room:         selectedRoom,
        checkInDate,
        checkOutDate,
        nights,
        totalPrice:   selectedRoom.price * nights,
      },
    });
};

  const starRating = '⭐'.repeat(hotel.starRating);

  const hotelFacilities = (() => {
    if (!hotel.facilities || !Array.isArray(hotel.facilities)) return [];
    return hotel.facilities.map(fid => {
      const found = facilitiesOptions.find(opt => opt.id === fid);
      return found ? found.label : fid;
    });
  })();

  return (
    <div className="cus-htl-details-container">

      {/* Header */}
      <div className="cus-htl-details-header">
        <Container className="d-flex align-items-center justify-content-between">
          <Button variant="outline-light" className="cus-htl-back-btn" onClick={() => navigate('/hotels')}>
            <ArrowLeft size={20} /> Back to Hotels
          </Button>
          <button
            className={`cus-htl-detail-wishlist-btn${isWishlisted ? ' saved' : ''}`}
            onClick={handleWishlistToggle}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <FaHeart /> {isWishlisted ? 'Saved' : 'Add to Wishlist'}
          </button>
        </Container>
      </div>
      <div className="cus-htl-hero-section">
        <img src={hotel.image} alt={hotel.name} className="cus-htl-hero-image" />
        <div className="cus-htl-hero-overlay">
          <Container>
            <h1 className="cus-htl-hotel-title">{hotel.name}</h1>
            <div className="cus-htl-hero-info">
              <span className="cus-htl-rating-badge">{starRating} {hotel.rating}</span>
              <span className="cus-htl-location-badge"><MapPin size={18} /> {hotel.location}</span>
            </div>
          </Container>
        </div>
      </div>
      <Container className="cus-htl-details-content">
        <Row>
          <Col lg={8} className="cus-htl-info-column">
            <Card className="cus-htl-info-card">
              <Card.Body>
                <h3 className="cus-htl-section-title"><Mountain size={24} /> Overview</h3>
                <p className="cus-htl-description">{hotel.description}</p>
              </Card.Body>
            </Card>
            <Card className="cus-htl-info-card">
              <Card.Body>
                <h3 className="cus-htl-section-title"><Wifi size={24} /> Hotel Facilities</h3>
                {hotelFacilities.length > 0 ? (
                  <div className="cus-htl-facilities-grid">
                    {hotelFacilities.map((facility, index) => (
                      <div key={index} className="cus-htl-facility-item">
                        <span className="cus-htl-facility-icon">✓</span> {facility}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No facilities listed.</p>
                )}
              </Card.Body>
            </Card>
            <Card className="cus-htl-info-card">
              <Card.Body>
                <h3 className="cus-htl-section-title"><Utensils size={24} /> Available Rooms</h3>
                <div className="cus-htl-rooms-container">
                  {hotel.rooms.map((room, index) => {
                    const isSelected    = selectedRoom?.id === room.id;
                    const roomImages    = room.images || [];
                    const amenitiesList = renderAmenities(room.amenities);
                    return (
                      <div
                        key={index}
                        className={`cus-htl-room-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedRoom(room)}
                      >
                        <div className="cus-htl-room-header">
                          <div>
                            <h5 className="cus-htl-room-name">{room.name}</h5>
                            <span className="cus-htl-room-type-label">{room.type}</span>
                            <div className="cus-htl-room-number">
                              <span className="cus-htl-room-number-label">Room No:</span>
                              <span className="cus-htl-room-number-value">{room.number || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        {roomImages.length > 0 ? (
                          <div className="cus-htl-room-images-strip">
                            {roomImages.slice(0, 3).map((src, i) => (
                              <div
                                key={i}
                                className="cus-htl-room-img-wrap"
                                onClick={(e) => { e.stopPropagation(); setZoomedImg(src); }}
                                title="Click to zoom"
                              >
                                <img src={src} alt={`Room ${i + 1}`} className="cus-htl-room-img" />
                                <div className="cus-htl-room-img-overlay">
                                  <ZoomIn size={13} color="white" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="cus-htl-no-images">No images available</div>
                        )}
                        <div className="cus-htl-room-price-row">
                          <span className="cus-htl-price-label">Price:</span>
                          <span className="cus-htl-room-price">Rs. {room.price?.toLocaleString()}/night</span>
                        </div>

                        <div className="cus-htl-room-details">
                          <div className="cus-htl-detail-item">
                            <span className="cus-htl-detail-label">Capacity: </span>
                            <span className="cus-htl-detail-value">{room.capacity} Persons</span>
                          </div>
                          <div className="cus-htl-detail-item">
                            <span className="cus-htl-detail-label">Floor:</span>
                            <span className="cus-htl-detail-value">{room.floor}</span>
                          </div>
                        </div>
                        {amenitiesList.length > 0 && (
                          <div className="cus-htl-room-amenities">
                            <span className="cus-htl-amenities-label">Amenities:</span>
                            <div className="cus-htl-amenities-list">
                              {amenitiesList.map((a, i) => (
                                <span key={i} className="cus-htl-amenity-badge">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {isSelected && <div className="cus-htl-selected-badge">Selected ✓</div>}
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>

          </Col>
          <Col lg={4} className="cus-htl-booking-column">
            <Card className="cus-htl-booking-card sticky-top">
              <Card.Body>
                <h4 className="cus-htl-booking-title">Book Your Stay</h4>
                <Form className="cus-htl-booking-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Check-in Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={checkInDate}
                      onChange={(e) => setCheckInDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="cus-htl-date-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Check-out Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={checkOutDate}
                      onChange={(e) => setCheckOutDate(e.target.value)}
                      min={checkInDate || new Date().toISOString().split('T')[0]}
                      className="cus-htl-date-input"
                    />
                  </Form.Group>

                  {selectedRoom && (
                    <div className="cus-htl-selected-room-info">
                      <p><strong>Selected Room:</strong></p>
                      <p>{selectedRoom.name} (Room #{selectedRoom.number})</p>
                      <p className="cus-htl-room-price-selected">
                        Rs. {selectedRoom.price?.toLocaleString()}/night
                      </p>
                    </div>
                  )}

                  <Button className="cus-htl-book-btn w-100" onClick={handleBookNow}>
                    Book Now
                  </Button>
                  <div className="cus-htl-booking-note">
                    <small>✓ Free cancellation up to 24 hours before check-in</small>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {zoomedImg && (
        <div className="cus-htl-zoom-backdrop" onClick={() => setZoomedImg(null)}>
          <div className="cus-htl-zoom-box" onClick={(e) => e.stopPropagation()}>
            <button className="cus-htl-zoom-close" onClick={() => setZoomedImg(null)}>
              <X size={22} />
            </button>
            <img src={zoomedImg} alt="Zoomed" className="cus-htl-zoom-img" />
          </div>
        </div>
      )}

      {showLoginModal && (
        <CustomerLoginForm onClose={() => setShowLoginModal(false)} />
      )}
    </div>
  );
}

export default HotelDetailsScreen;